import { SearchOff } from '@mui/icons-material';
import { Box, Stack, Typography } from '@mui/material';
import { FullSizePaper } from 'components';
import animationData from 'apps/portal/components/icons/lotties/cloudTransfer.json';

import { useMeta } from 'hooks/useMeta';

import { forwardRef, IframeHTMLAttributes, useMemo } from 'react';
import { CSSLinearProgress } from './CSSLinearProgress';
import { useS3Document } from '../hooks/useS3Document';
import LoadingBox from './LoadingBox';

type S3DocumentViewerProps = {
  s3Key: string;
  showToolbar?: boolean;
} & IframeHTMLAttributes<HTMLIFrameElement>;

export const S3DocumentViewer = forwardRef<HTMLIFrameElement, S3DocumentViewerProps>(
  ({ s3Key, showToolbar = true, ...props }, ref) => {
    const options = useMemo(() => {
      const ext = s3Key.split('.').pop();
      return {
        contentDisposition: 'inline',
        contentType: ext === 'pdf' ? 'application/pdf' : `image/${ext}`,
      };
    }, [s3Key]);

    const { result: url, ...documentMeta } = useS3Document(s3Key, options);
    const { loading } = useMeta(documentMeta);

    const src = url ? `${url}#view=FitH&toolbar=${showToolbar ? 1 : 0}` : null;
    const documentKind = s3Key.endsWith('pdf') ? 'document' : 'image';

    return loading ? (
      <>
        <CSSLinearProgress />
        <LoadingBox animationData={animationData} message={`Downloading ${documentKind}...`} />
      </>
    ) : !src ? (
      <FullSizePaper sx={{ width: '100%', height: '100%' }}>
        <Stack alignItems="center" justifyContent="center" width="100%" height="100%" gap={3}>
          <Typography variant="h6">
            We searched everywhere, but couldn't find that {documentKind}
          </Typography>

          <SearchOff color="disabled" sx={{ fontSize: 100 }} />
        </Stack>
      </FullSizePaper>
    ) : (
      <Box
        component="iframe"
        {...props}
        ref={ref}
        src={src}
        srcDoc={s3Key.endsWith('pdf') ? undefined : `<img src="${url}" style="width: 100%;" />`}
        style={{ display: 'flex', flexGrow: 1, ...props.style }}
      />
    );
  }
);

export default S3DocumentViewer;
